import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatMiles, formatPrice } from "@/lib";
import {
  calculateDynamicRate,
  calculateTotalPayout,
} from "@/lib/miles-calculations";
import { serviceHost } from "@/services";
import {
  TransactionTypes,
  useDashboardStore,
  useUserCredentialsStore,
} from "@/stores";
import {
  buildCustomerPortalAnalyticsPayload,
  pushCustomerPortalAnalyticsEvent,
  savePendingDealSubmittedPayload,
} from "@/utils";
import { CheckCircle, Plane, Shield, Star, TicketsPlane } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { EmailDialog } from "./EmailDialog";
import { NotEnoughMilesDialog } from "./NotEnoughMilesDialog";

interface TotalPayoutProps {
  onEmailError: (error: boolean) => void;
  onSecureQuote: () => void | Promise<void>;
}

export const TotalPayout = ({
  onEmailError,
  onSecureQuote,
}: TotalPayoutProps) => {
  const transactionType = useUserCredentialsStore((s) => s.transactionType);
  const fallbackEmail = useUserCredentialsStore((s) => s.email);
  const fallbackFirstName = useUserCredentialsStore((s) => s.firstName);
  const fallbackLastName = useUserCredentialsStore((s) => s.lastName);
  const fallbackPhoneNumber = useUserCredentialsStore((s) => s.phoneNumber);

  const navigate = useNavigate();

  const program = useDashboardStore((s) => s.program);
  const functionsService = serviceHost.getFunctionsService();

  const handleSecureQuote = async () => {
    await onSecureQuote();
  };

  const handleEmailQuote = async () => {
    if (!program) {
      return;
    }
    try {
      await functionsService.emailQuote({
        temporaryDealId: program.id,
        quantity: program.sanitizedMiles,
      });

      const sharedPayloadArgs = {
        fallbackProfile: {
          email: fallbackEmail,
          firstName: fallbackFirstName,
          lastName: fallbackLastName,
          phoneNumber: fallbackPhoneNumber,
        },
        offerType: transactionType,
        milesPointsType: program.name,
        amount_of_miles: program.sanitizedMiles,
        amount: program.totalPayout,
        bonusTier: program.loyaltyStatus,
      };

      pushCustomerPortalAnalyticsEvent({
        payload: buildCustomerPortalAnalyticsPayload({
          event: "deal_approve_offer",
          ...sharedPayloadArgs,
        }),
      });

      savePendingDealSubmittedPayload({
        payload: buildCustomerPortalAnalyticsPayload({
          event: "deal_submitted",
          ...sharedPayloadArgs,
        }),
      });

      navigate("/email-thank-you");
    } catch (error) {
      console.error("Email quote error:", error);
      onEmailError(true);
    }
  };

  const handleFollowUpEmail = async () => {
    if (!program) {
      return;
    }
    try {
      await functionsService.followUpEmail({
        temporaryDealId: program.id,
        quantity: program.sanitizedMiles,
      });
      navigate("/follow-up-email-thank-you");
    } catch (error) {
      console.error("Follow up email error:", error);
      onEmailError(true);
    }
  };

  if (!program) {
    return null;
  }

  const dynamicRate = calculateDynamicRate({
    miles: program.sanitizedMiles,
    transactionType,
    sellPrices: program.sellPrices,
    buyPrices: program.buyPrices,
    fallbackRate: 1,
  });

  const { totalPayout, promoCodeDiscount } = calculateTotalPayout({
    totalMiles: program.sanitizedMiles,
    dynamicRate,
    loyaltyStatus: program.loyaltyStatus,
    transactionType,
    promoCode: program.promoCode,
  });

  return (
    <div className="flex flex-col flex-1 gap-7 xl:gap-14 overflow-hidden h-fit w-full">
      <Card className="bg-white shadow-lg shadow-accent/10 border-[#DEF5FD] rounded-2xl xl:rounded-3xl overflow-hidden h-fit pt-3 xl:pt-6 pb-0 gap-3 xl:gap-6">
        <CardHeader className="px-4 xl:px-6">
          <div className="flex w-full h-full justify-between items-center">
            <CardTitle className="text-[20px] md:text-[28px] xl:text-[32px] font-semibold">
              Your Total Payout
            </CardTitle>
            <div className="bg-[#EAFCFF] text-[#0566FF] px-2 xl:px-4 py-1 xl:py-2 rounded-full uppercase font-semibold text-[10px] md:text-[14px]">
              {transactionType === TransactionTypes.SELL && "Sell miles"}{" "}
              {transactionType === TransactionTypes.BUY && "Buy miles"}
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 p-0">
          <div className="rounded-b-lg p-3 px-4 xl:p-6 flex flex-col gap-3 xl:gap-6 bg-[#F9F9F9]">
            <div>
              <div className="flex flex-col gap-4">
                <div key={program.programId} className="flex flex-col gap-2">
                  <div className="flex flex-col gap-1">
                    <div className="w-full flex justify-between items-start gap-4">
                      <div className="flex flex-row gap-2 items-center">
                        <Plane className="w-5 h-5" />
                        <span className="text-[16px] md:text-[20px]">
                          Airline
                        </span>
                      </div>
                      <div className="text-[16px] md:text-[20px] text-right">
                        {program.airline}
                      </div>
                    </div>
                    <div className="w-full flex justify-between items-center">
                      <div className="flex flex-row gap-2 items-center">
                        <TicketsPlane className="w-5 h-5" />
                        <span className="text-[16px] md:text-[20px]">
                          Miles
                        </span>
                      </div>
                      <div className="text-[16px] md:text-[20px] break-words text-left">
                        {program.sanitizedMiles >= 1000
                          ? formatMiles(program.sanitizedMiles)
                          : program.sanitizedMiles}
                      </div>
                    </div>
                    <div className="w-full flex justify-between items-center">
                      <div className="text-[16px] md:text-[20px]">Rate</div>
                      <div className="text-[16px] md:text-[20px] break-words text-left">
                        {formatPrice({
                          price: dynamicRate * 100,
                          fractionDigits: 2,
                        })}
                        /100 miles
                      </div>
                    </div>
                    {program.loyaltyStatus && (
                      <div className="w-full flex justify-between items-center">
                        <div className="flex flex-row gap-2 items-center">
                          <span className="text-[16px] md:text-[20px]">
                            Loyalty Bonus{" "}
                            <span className="text-blue-600">
                              {program.loyaltyStatus.name}
                            </span>
                          </span>
                        </div>
                        <div className="text-[16px] md:text-[20px] text-blue-600 break-words text-left">
                          {formatPrice({
                            price:
                              (program.loyaltyStatus.percentage / 100) *
                              program.sanitizedMiles *
                              program.ratePerMile,
                            fractionDigits: 2,
                          })}
                        </div>
                      </div>
                    )}
                    {program.promoCode && promoCodeDiscount !== 0 && (
                      <div className="w-full flex justify-between items-center">
                        <div className="flex flex-row gap-2 items-center">
                          <span className="text-[16px] md:text-[20px]">
                            Promo Code{" "}
                            <span className="text-blue-600">
                              {program.promoCode.name}
                            </span>
                          </span>
                        </div>
                        <div className={`text-[16px] md:text-[20px] break-words text-left text-blue-600`}>
                          {formatPrice({
                            price: promoCodeDiscount,
                            fractionDigits: 2,
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex w-full justify-between mt-4 pt-4 border-t text-xl xl:text-[24px] font-bold items-center">
                <div>
                  {transactionType === TransactionTypes.SELL &&
                    "You'll Receive:"}
                  {transactionType === TransactionTypes.BUY && "Your offer is:"}
                </div>
                <div className="break-words text-left">
                  {formatPrice({ price: totalPayout })}
                </div>
              </div>
            </div>

            <Button
              onClick={handleSecureQuote}
              className="bg-primary/90 hover:bg-primary rounded-sm py-6"
            >
              Secure My Quote & Get Paid
            </Button>

            <div className="flex flex-col gap-0">
              <EmailDialog onEmailDetails={handleEmailQuote} />
              <NotEnoughMilesDialog followUpEmail={handleFollowUpEmail} />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-center gap-[14px] md:gap-12 text-nowrap flex-wrap pb-6 md:pb-0">
        <div className="flex items-center gap-1 md:gap-3">
          <CheckCircle className="w-6 h-6 text-black" />
          <span className="text-black font-medium text-[16px] md:text-lg">
            No Hidden Fees
          </span>
        </div>
        <div className="flex items-center gap-1 md:gap-3">
          <Shield className="w-6 h-6 text-black" />
          <span className="text-black font-medium text-[16px] md:text-lg">
            100% Secure
          </span>
        </div>
        <div className="flex items-center gap-1 md:gap-3">
          <Star className="w-6 h-6 text-black" />
          <span className="text-black font-medium text-[16px] md:text-lg">
            Best Rate
          </span>
        </div>
      </div>
    </div>
  );
};
